/* ═══════════════════════════════════════════════════════════════════════════
   feral-gift-wallet — a FERAL gift card goes in the bag, not in a copy-paste.

   Until 2026-10-20 a gift card only redeemed at checkout: the bag's "Add a
   code" answered a card with "Not valid for this order" (the Ajax /discount/
   route never takes one), and the recipient's card page said "Spend it" and
   then left them to copy a 16-character code into checkout themselves.

   The Storefront API's cart takes gift cards (cartGiftCardCodesAdd), it works
   with NO token on the store's own domain, and the Ajax cart's token IS a
   Storefront cart id. MEASURED 2026-10-20 on live with a £1 TEST card, a DE
   session: added to the Ajax cart's gid → appliedGiftCards −€1.22, survived a
   /cart/update.js, and /checkout opened at €13.78 for a €15 thong.

   So:
   - the bag's code box tries a refused discount code as a gift card;
   - the card page's "Shop with it" keeps the card on this device
     (localStorage, never a URL, so it reaches no analytics), and the bag puts
     it on by itself whenever there is something to spend it on;
   - an applied card is drawn under the subtotal: the card, what comes off,
     what is left to pay, what stays on the card;
   - FeralGift.balance(code) reads a balance off a throwaway cart, for the
     balance check on /pages/gift-cards.

   What Shopify does not allow and this does not try: a gift card goes on NO
   cart that holds a gift card, not even against the other lines (MEASURED
   2026-10-20: a £10 card + a thong cart applied nothing, no error). So such a
   bag says so, and the kept card waits for the next bag.
   Source + rollback: ~/gift-cards (README, "The card in the bag").
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.FeralGift) return;
  var API = '/api/2025-07/graphql.json';
  var KEY = 'feral.giftcard';
  var KEEP_DAYS = 180;

  /* ── storage: one card per device, the code never leaves it ────────────── */
  function kept() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!v || !v.c) return null;
      if (Date.now() - (v.t || 0) > KEEP_DAYS * 864e5) { forget(); return null; }
      return v;
    } catch (e) { return null; }
  }
  function keep(code, extra) {
    var v = { c: clean(code), l: clean(code).slice(-4).toLowerCase(), t: Date.now() };
    for (var k in extra || {}) v[k] = extra[k];
    try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {}
    return v;
  }
  function forget() { try { localStorage.removeItem(KEY); } catch (e) {} }
  function clean(code) { return String(code || '').replace(/[\s-]+/g, ''); }

  /* ── the Storefront API, tokenless ─────────────────────────────────────── */
  function sf(query, variables) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: query, variables: variables || {} }),
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('storefront ' + r.status);
      return r.json();
    }).then(function (j) {
      if (j.errors && j.errors.length) throw new Error(j.errors[0].message || 'storefront error');
      return j.data;
    });
  }
  var CARDS = 'appliedGiftCards { id lastCharacters balance { amount currencyCode } amountUsed { amount currencyCode } }' +
              ' cost { totalAmount { amount currencyCode } }';

  function ajaxCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }
  function gid(cart) { return 'gid://shopify/Cart/' + cart.token; }
  // Shopify applies a gift card only to a cart with no gift card in it.
  function spendable(cart) {
    var items = cart.items || [];
    return items.length > 0 && !items.some(function (i) { return i.gift_card; });
  }
  function read(cart) {
    return sf('query($id: ID!) { cart(id: $id) { ' + CARDS + ' } }', { id: gid(cart) })
      .then(function (d) { return d.cart; });
  }

  // → { ok: true, cart } | { ok: false, why: 'cardonly' | 'invalid' | 'empty' }
  function add(code) {
    code = clean(code);
    if (!code) return Promise.resolve({ ok: false, why: 'invalid' });
    return ajaxCart().then(function (cart) {
      if (!cart.item_count) return { ok: false, why: 'empty' };
      return sf('mutation($id: ID!, $c: [String!]!) { cartGiftCardCodesAdd(cartId: $id, giftCardCodes: $c) {' +
                ' cart { ' + CARDS + ' } userErrors { code message } } }', { id: gid(cart), c: [code] })
        .then(function (d) {
          var res = d.cartGiftCardCodesAdd || {};
          var c = res.cart;
          var hit = c && (c.appliedGiftCards || []).some(function (g) {
            return g.lastCharacters.toLowerCase() === code.slice(-4).toLowerCase();
          });
          if (hit) return { ok: true, cart: c };
          return { ok: false, why: spendable(cart) ? 'invalid' : 'cardonly' };
        });
    });
  }

  function remove(id) {
    return ajaxCart().then(function (cart) {
      return sf('mutation($id: ID!, $g: [ID!]!) { cartGiftCardCodesRemove(cartId: $id, appliedGiftCardIds: $g) {' +
                ' cart { ' + CARDS + ' } userErrors { message } } }', { id: gid(cart), g: [id] });
    });
  }

  // A throwaway cart in this visitor's country, so the balance reads in the
  // currency they would spend it in. → { ok, amount, currency } | { ok: false }
  function balance(code) {
    code = clean(code);
    var cc = (window.Shopify && Shopify.country) || 'GB';
    return sf('mutation($c: [String!]!, $cc: CountryCode!) { cartCreate(input: { giftCardCodes: $c,' +
              ' buyerIdentity: { countryCode: $cc } }) { cart { ' + CARDS + ' } } }', { c: [code], cc: cc })
      .then(function (d) {
        var g = d.cartCreate && d.cartCreate.cart && d.cartCreate.cart.appliedGiftCards[0];
        if (!g) return { ok: false };
        return { ok: true, amount: +g.balance.amount, currency: g.balance.currencyCode, last4: g.lastCharacters };
      });
  }

  /* ── money, in the theme's own format ──────────────────────────────────────
     The drawer prints {{ 123456 | money }} on the slot ("€1.234,56", "£1,234.56",
     "1 234,56 kr"); prefix, separators and suffix are read off it, so a gift
     card line can never disagree with the subtotal above it.                 */
  function money(n, sample) {
    var m = /^([^\d]*)1(\D?)234(\D)56(.*)$/.exec(String(sample || '').trim());
    var pre = m ? m[1] : '', th = m ? m[2] : ',', dec = m ? m[3] : '.', suf = m ? m[4] : '';
    var p = Math.abs(n).toFixed(2).split('.');
    p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
    return pre + p[0] + dec + p[1] + suf;
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ── the bag ───────────────────────────────────────────────────────────── */
  var tried = {};        // cart token → the kept card was offered once this page load
  var painting = 0;

  function paint() {
    var slot = document.querySelector('[data-feral-giftcards]');
    if (!slot) return Promise.resolve();
    var mine = ++painting;
    return ajaxCart().then(function (cart) {
      if (mine !== painting) return;
      if (!cart.item_count) { draw(slot, null, cart); return; }
      var k = kept();
      return read(cart).then(function (c) {
        if (mine !== painting) return;
        var on = (c && c.appliedGiftCards) || [];
        var has = k && on.some(function (g) { return g.lastCharacters.toLowerCase() === k.l; });
        if (k && !has && spendable(cart) && !tried[cart.token]) {
          tried[cart.token] = true;
          return add(k.c).then(function (r) {
            if (mine !== painting) return;
            if (r.ok) { draw(slot, r.cart, cart); return; }
            draw(slot, c, cart);
            // Refused on a bag it should fit: forget it only once a balance
            // read says it is spent or gone, never on one refusal.
            balance(k.c).then(function (b) { if (!b.ok || !(b.amount > 0)) forget(); }, function () {});
          });
        }
        draw(slot, c, cart, k && !has && !spendable(cart));
      });
    }).catch(function () { /* the bag still works; checkout takes the code */ });
  }

  function draw(slot, c, cart, cardOnly) {
    var on = (c && c.appliedGiftCards) || [];
    var sample = slot.getAttribute('data-money-sample');
    if (!on.length) {
      slot.innerHTML = cardOnly
        ? '<p class="feral-gc__hint">Your gift card can’t go on a bag with a gift card in it. Check that one out on its own, and yours comes off everything else.</p>'
        : '';
      slot.hidden = !cardOnly;
      return;
    }
    var html = '';
    on.forEach(function (g) {
      html += '<div class="feral-gc__row">' +
        '<span class="feral-gc__name"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2.5"/><path d="M3 10h18"/></svg>' +
        'Gift card <span class="feral-gc__last">··' + esc(g.lastCharacters.toUpperCase()) + '</span></span>' +
        '<b class="feral-gc__off">−' + esc(money(+g.amountUsed.amount, sample)) + '</b>' +
        '<button type="button" class="feral-gc__rm" data-feral-gc-remove="' + esc(g.id) + '" data-last="' + esc(g.lastCharacters) +
        '" aria-label="Take gift card ending ' + esc(g.lastCharacters.toUpperCase()) + ' off">Remove</button>' +
        '</div>';
    });
    var left = on.reduce(function (s, g) { return s + +g.balance.amount; }, 0);
    html += '<div class="feral-gc__pay"><span>To pay</span><b>' + esc(money(+c.cost.totalAmount.amount, sample)) + '</b></div>';
    if (left > 0.004) html += '<p class="feral-gc__hint">' + esc(money(left, sample)) + ' stays on the card for next time.</p>';
    slot.innerHTML = html;
    slot.hidden = false;
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-feral-gc-remove]');
    if (!b || b.disabled) return;
    b.disabled = true;
    b.textContent = 'Removing…';
    var k = kept();
    if (k && k.l === String(b.getAttribute('data-last')).toLowerCase()) forget();
    remove(b.getAttribute('data-feral-gc-remove')).then(paint, paint);
  });

  // Every drawer render replaces the slot; a fresh slot has no data-drawn.
  var queued = 0;
  function soon() { clearTimeout(queued); queued = setTimeout(paint, 120); }
  function watch() {
    var host = document.querySelector('cart-drawer') || document.body;
    new MutationObserver(function () {
      var slot = document.querySelector('[data-feral-giftcards]');
      if (slot && !slot.hasAttribute('data-drawn')) { slot.setAttribute('data-drawn', ''); soon(); }
    }).observe(host, { childList: true, subtree: true });
    var slot = document.querySelector('[data-feral-giftcards]');
    if (slot) { slot.setAttribute('data-drawn', ''); soon(); }
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) window.subscribe(PUB_SUB_EVENTS.cartUpdate, soon);
  }

  /* ── arriving from the card page: say once that the card came with them ─ */
  function welcome() {
    var k = kept();
    if (!k || !k.fresh) return;
    delete k.fresh;
    try { localStorage.setItem(KEY, JSON.stringify(k)); } catch (e) {}
    if (/\/gift_cards\//.test(location.pathname)) return;
    var t = document.createElement('div');
    t.className = 'feral-gc-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = '<b>' + (k.a ? esc(k.a) + ' gift card' : 'Your gift card') + ' is with you.</b>' +
                  '<span>It comes off in your bag. No code to copy.</span>';
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-on'); });
    setTimeout(function () { t.classList.remove('is-on'); setTimeout(function () { t.remove(); }, 500); }, 6000);
  }

  /* ── styles: the bag's own tokens, Inter, red only as the minus sign's dot ─ */
  var css = '' +
    '[data-feral-giftcards]{display:grid;gap:10px;margin-top:12px}' +
    '[data-feral-giftcards][hidden]{display:none}' +
    '.feral-gc__row{display:flex;align-items:center;gap:12px;min-height:44px;font-size:14px;line-height:1.3}' +
    '.feral-gc__name{display:inline-flex;align-items:center;gap:8px;flex:1;min-width:0;color:inherit}' +
    '.feral-gc__name svg{width:18px;height:18px;flex:none;fill:none;stroke:currentColor;stroke-width:1.7}' +
    '.feral-gc__last{opacity:.72;font-variant-numeric:tabular-nums;letter-spacing:.04em}' +
    '.feral-gc__off{font-weight:600;font-variant-numeric:tabular-nums}' +
    '.feral-gc__rm{min-height:44px;padding:0 2px 0 8px;background:none;border:0;color:inherit;opacity:.72;font:inherit;font-size:12px;letter-spacing:.08em;text-transform:uppercase;text-decoration:underline;text-underline-offset:3px;cursor:pointer}' +
    '.feral-gc__pay{display:flex;justify-content:space-between;align-items:baseline;padding-top:10px;border-top:1px solid rgba(255,255,255,.12);font-size:14px;letter-spacing:.08em;text-transform:uppercase}' +
    '.feral-gc__pay b{font-size:22px;letter-spacing:0;font-variant-numeric:tabular-nums}' +
    '[data-feral-giftcards] .feral-gc__hint{margin:0;font-size:13px;line-height:1.45;opacity:.72}' +
    '.feral-gc-toast{position:fixed;left:50%;bottom:calc(20px + env(safe-area-inset-bottom));z-index:2147483000;display:grid;gap:2px;width:min(420px,calc(100vw - 32px));padding:14px 18px;border-radius:14px;background:#f5f5f7;color:#0c0c0c;font:14px/1.4 FeralInter,Inter,system-ui,sans-serif;box-shadow:0 18px 40px rgba(0,0,0,.45);transform:translate(-50%,24px);opacity:0;transition:transform .45s cubic-bezier(.2,.8,.2,1),opacity .45s}' +
    '.feral-gc-toast.is-on{transform:translate(-50%,0);opacity:1}' +
    '.feral-gc-toast b{font-weight:600}' +
    '@media (prefers-reduced-motion:reduce){.feral-gc-toast{transition:none}}' +
    '@media (hover:hover){.feral-gc__rm:hover{opacity:1}}' +
    '.feral-gc__rm:focus-visible{outline:2px solid currentColor;outline-offset:2px;border-radius:4px}';
  var st = document.createElement('style');
  st.textContent = css;
  document.head.appendChild(st);

  window.FeralGift = { add: add, balance: balance, keep: keep, forget: forget, kept: kept, paint: paint, money: money };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { watch(); welcome(); });
  else { watch(); welcome(); }
})();
